import DSModal from "@/componentsDS/Modal/Modal.jsx";
import DSCardConfigure from "@/componentsDS/Modal/CardConfigure.jsx";
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import rem from "@/utils/rem.js";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

const ROLE_OPTIONS = [
	{ value: "PROVIDER", label: "Provider", payloadKey: "producedBy" },
	{ value: "CONSUMER", label: "Consumer", payloadKey: "consumedBy" },
	{ value: "PROVIDE_ON_BEHALF", label: "Provide on Behalf", payloadKey: "provideOnBehalf" },
	{ value: "CONSUME_ON_BEHALF", label: "Consume on Behalf", payloadKey: "consumeOnBehalf" },
	{ value: "PROVIDE_ON_BEHALF_OF_PRINCIPAL", label: "Provide on Behalf of Principal", payloadKey: "provideOnBehalfOfPrincipal" },
	{ value: "CONSUME_ON_BEHALF_OF_PRINCIPAL", label: "Consume on Behalf of Principal", payloadKey: "consumeOnBehalfOfPrincipal" },
];

const DEFAULT_USE_CASE_ID = "use_case_default";
const DEFAULT_PERSONA_ID = "persona_default";

function buildPayload({ useCase, personaIds, roles, dataElementId }) {
	const dataElement = { id: dataElementId };
	for (const role of ROLE_OPTIONS) {
		dataElement[role.payloadKey] = roles.includes(role.value) ? [...personaIds] : [];
	}
	return [
		{
			id: useCase.id,
			name: useCase.name,
			personas: [],
			dataElements: [dataElement],
		},
	];
}

function LinkUseCasePersonaModal({ visible, hide, dataElement, onSuccess }) {
	const { linkUseCasePersona, fetchUseCases, fetchPersonas } = useDataSchemaVersioning();

	const [useCases, setUseCases] = useState([]);
	const [personas, setPersonas] = useState([]);
	const [useCaseId, setUseCaseId] = useState(DEFAULT_USE_CASE_ID);
	const [personaIds, setPersonaIds] = useState([DEFAULT_PERSONA_ID]);
	const [roles, setRoles] = useState(["PROVIDER"]);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);
	const [bootstrapping, setBootstrapping] = useState(false);

	useEffect(() => {
		if (!visible) return;
		let cancelled = false;
		setBootstrapping(true);
		setSubmitError(null);
		Promise.all([fetchUseCases(), fetchPersonas()])
			.then(([uc, p]) => {
				if (cancelled) return;
				setUseCases(Array.isArray(uc) ? uc : []);
				setPersonas(Array.isArray(p) ? p : []);
			})
			.finally(() => {
				if (!cancelled) setBootstrapping(false);
			});
		return () => { cancelled = true; };
	}, [visible]);

	const useCaseOptions = useMemo(() => {
		return useCases.map((uc) => ({
			id: uc.useCaseId ?? uc.id,
			name: uc.name ?? uc.useCaseName ?? uc.useCaseId,
		})).filter((u) => u.id);
	}, [useCases]);

	const personaOptions = useMemo(() => {
		return personas.map((p) => ({
			id: p.personaId ?? p.id,
			name: p.name ?? p.personaName ?? p.personaId,
		})).filter((p) => p.id);
	}, [personas]);

	const selectedUseCase = useMemo(() => {
		const match = useCaseOptions.find((u) => u.id === useCaseId);
		if (match) return match;
		return { id: useCaseId, name: useCaseId };
	}, [useCaseOptions, useCaseId]);

	function togglePersona(id) {
		setPersonaIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	}

	function toggleRole(value) {
		setRoles((prev) =>
			prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]
		);
	}

	const canSubmit =
		!submitting &&
		!bootstrapping &&
		useCaseId &&
		personaIds.length > 0 &&
		roles.length > 0 &&
		dataElement?.dataElementId;

	async function handleConfirm() {
		if (!canSubmit) return;
		setSubmitError(null);
		setSubmitting(true);
		const payload = buildPayload({
			useCase: selectedUseCase,
			personaIds,
			roles,
			dataElementId: dataElement.dataElementId,
		});
		const result = await linkUseCasePersona(payload);
		setSubmitting(false);
		if (result?.error) {
			const message =
				result.error?.message ||
				(typeof result.error === "string" ? result.error : "Failed to link use case.");
			setSubmitError(message);
			return;
		}
		if (typeof onSuccess === "function") onSuccess();
	}

	if (!visible) return null;

	return (
		<DSModal visible={visible} close={hide} variant="small" dismissClickOutsideModal={false}>
			<DSCardConfigure style={{ minWidth: 600 }}>
				<DSCardConfigure.Header>
					<DSCardConfigure.Header.Title>Link Use Case</DSCardConfigure.Header.Title>
					<DSCardConfigure.Header.Close>
						<button className="dsv-icon-btn" onClick={hide} title="Close">&times;</button>
					</DSCardConfigure.Header.Close>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						<p className="dsv-confirm__subtitle">
							Attach <strong>{dataElement?.elementName || dataElement?.dataElementId}</strong> to a use case and persona(s) so that subscriptions can be created.
						</p>

						{bootstrapping ? (
							<p className="dsv-promote__hint">Loading use cases and personas…</p>
						) : (
							<>
								<div className="dsv-confirm__section">
									<h3 className="dsv-confirm__section-title">Use Case</h3>
									<select
										className="dsv-modal__select"
										value={useCaseId}
										onChange={(e) => setUseCaseId(e.target.value)}
									>
										{useCaseOptions.length === 0 && (
											<option value={DEFAULT_USE_CASE_ID}>{DEFAULT_USE_CASE_ID}</option>
										)}
										{useCaseOptions.map((uc) => (
											<option key={uc.id} value={uc.id}>{uc.name} ({uc.id})</option>
										))}
									</select>
								</div>

								<div className="dsv-confirm__section">
									<h3 className="dsv-confirm__section-title">Personas</h3>
									{personaOptions.length === 0 ? (
										<p className="dsv-promote__hint">
											No personas configured for this environment — seed at least one before linking.
										</p>
									) : (
										<div style={{ display: "flex", flexDirection: "column", gap: rem(6) }}>
											{personaOptions.map((p) => (
												<label key={p.id} style={{ display: "flex", alignItems: "center", gap: rem(8) }}>
													<input
														type="checkbox"
														checked={personaIds.includes(p.id)}
														onChange={() => togglePersona(p.id)}
													/>
													<span>{p.name} <span style={{ color: "#888" }}>({p.id})</span></span>
												</label>
											))}
										</div>
									)}
								</div>

								<div className="dsv-confirm__section">
									<h3 className="dsv-confirm__section-title">Roles</h3>
									<p className="dsv-promote__hint">
										Selected personas will be added under each chosen role.
									</p>
									<div style={{ display: "flex", flexDirection: "column", gap: rem(6) }}>
										{ROLE_OPTIONS.map((r) => (
											<label key={r.value} style={{ display: "flex", alignItems: "center", gap: rem(8) }}>
												<input
													type="checkbox"
													checked={roles.includes(r.value)}
													onChange={() => toggleRole(r.value)}
												/>
												<span>{r.label}</span>
											</label>
										))}
									</div>
								</div>

								{submitError && (
									<div className="dsv-confirm__section">
										<p className="dsv-promote__hint" style={{ color: "#b45309" }}>
											{submitError}
										</p>
									</div>
								)}
							</>
						)}
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<button className="dsv-btn dsv-btn--ghost" onClick={hide} disabled={submitting}>Cancel</button>
						<button
							className="dsv-btn dsv-btn--primary"
							style={{ marginLeft: rem(12) }}
							onClick={handleConfirm}
							disabled={!canSubmit}
						>
							{submitting ? "Linking…" : "Confirm"}
						</button>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

LinkUseCasePersonaModal.propTypes = {
	visible: PropTypes.bool,
	hide: PropTypes.func,
	dataElement: PropTypes.shape({
		dataElementId: PropTypes.string,
		elementName: PropTypes.string,
	}),
	onSuccess: PropTypes.func,
};

export default LinkUseCasePersonaModal;
export { buildPayload, ROLE_OPTIONS };
